/**
 * Sign Stamp Storage Utility
 * Manages persistent sign/stamp storage in localStorage with deduplication support
 */

import { logger } from './logger';

const DEFAULT_SIGN_STAMP_KEY = 'default-invoice-sign-stamp';
const DEFAULT_SIGN_STAMP_NAME_KEY = 'default-invoice-sign-stamp-name';
const DEFAULT_SIGN_STAMP_ROLE_KEY = 'default-invoice-sign-stamp-role';
const SIGN_STAMP_HASHES_KEY = 'invoice-sign-stamp-hashes';

/**
 * Calculates a simple hash from a base64 string for deduplication
 */
function hashSignStamp(signStampData: string): string {
  // Remove the data:image/...;base64, prefix if present
  const base64Data = signStampData.split(',')[1] || signStampData;

  // Simple hash function for base64 strings
  let hash = 0;
  for (let i = 0; i < base64Data.length; i++) {
    const char = base64Data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return Math.abs(hash).toString(36);
}

/**
 * Retrieves the default sign/stamp from localStorage
 * @returns Base64 encoded sign/stamp data or null if not set
 */
export function getDefaultSignStamp(): string | null {
  try {
    const signStamp = localStorage.getItem(DEFAULT_SIGN_STAMP_KEY);
    logger.info('[signStampStorage] getDefaultSignStamp:', signStamp ? 'Found' : 'Not found');
    return signStamp;
  } catch (error) {
    logger.error('[signStampStorage] Failed to retrieve default sign/stamp:', error);
    return null;
  }
}

/**
 * Saves a sign/stamp as the default sign/stamp in localStorage
 * @param signStampData - Base64 encoded sign/stamp data
 * @returns The hash of the stored sign/stamp
 */
export function setDefaultSignStamp(signStampData: string): string {
  try {
    const signStampHash = hashSignStamp(signStampData);
    logger.info('[signStampStorage] setDefaultSignStamp: Saving sign/stamp with hash:', signStampHash);

    // Store the sign/stamp
    localStorage.setItem(DEFAULT_SIGN_STAMP_KEY, signStampData);

    // Update the hash registry
    const hashes = getSignStampHashes();
    hashes[signStampHash] = DEFAULT_SIGN_STAMP_KEY;
    localStorage.setItem(SIGN_STAMP_HASHES_KEY, JSON.stringify(hashes));

    logger.info('[signStampStorage] Default sign/stamp saved successfully');
    return signStampHash;
  } catch (error) {
    logger.error('[signStampStorage] Failed to save default sign/stamp:', error);
    throw error;
  }
}

/**
 * Removes the default sign/stamp from localStorage
 */
export function clearDefaultSignStamp(): void {
  try {
    const currentSignStamp = localStorage.getItem(DEFAULT_SIGN_STAMP_KEY);
    logger.info('[signStampStorage] clearDefaultSignStamp: Clearing sign/stamp');

    if (currentSignStamp) {
      const signStampHash = hashSignStamp(currentSignStamp);

      // Remove the sign/stamp
      localStorage.removeItem(DEFAULT_SIGN_STAMP_KEY);

      // Update the hash registry
      const hashes = getSignStampHashes();
      delete hashes[signStampHash];
      localStorage.setItem(SIGN_STAMP_HASHES_KEY, JSON.stringify(hashes));

      logger.info('[signStampStorage] Default sign/stamp cleared successfully');
    }
  } catch (error) {
    logger.error('[signStampStorage] Failed to clear default sign/stamp:', error);
  }
}

/**
 * Checks if a sign/stamp matches the default sign/stamp
 * @param signStampData - Base64 encoded sign/stamp data to check
 * @returns true if the sign/stamp matches the default sign/stamp
 */
export function isDefaultSignStamp(signStampData: string): boolean {
  const defaultSignStamp = getDefaultSignStamp();
  if (!defaultSignStamp || !signStampData) {
    return false;
  }

  const signStampHash = hashSignStamp(signStampData);
  const defaultHash = hashSignStamp(defaultSignStamp);

  return signStampHash === defaultHash;
}

/**
 * Gets the hash of a sign/stamp for storage optimization
 * @param signStampData - Base64 encoded sign/stamp data
 * @returns The hash of the sign/stamp
 */
export function getSignStampHash(signStampData: string): string {
  return hashSignStamp(signStampData);
}

/**
 * Retrieves all sign/stamp hashes from localStorage
 * @returns Object mapping sign/stamp hashes to storage keys
 */
function getSignStampHashes(): Record<string, string> {
  try {
    const hashesJson = localStorage.getItem(SIGN_STAMP_HASHES_KEY);
    return hashesJson ? JSON.parse(hashesJson) : {};
  } catch (error) {
    console.error('Failed to retrieve sign/stamp hashes from localStorage:', error);
    return {};
  }
}

/**
 * Checks if a sign/stamp already exists in storage (for deduplication)
 * @param signStampData - Base64 encoded sign/stamp data to check
 * @returns The storage key if the sign/stamp exists, null otherwise
 */
export function signStampExistsInStorage(signStampData: string): string | null {
  const signStampHash = hashSignStamp(signStampData);
  const hashes = getSignStampHashes();

  return hashes[signStampHash] || null;
}

/**
 * Retrieves the default sign/stamp name from localStorage
 * @returns Sign/stamp name or null if not set
 */
export function getDefaultSignStampName(): string | null {
  try {
    const name = localStorage.getItem(DEFAULT_SIGN_STAMP_NAME_KEY);
    logger.info('[signStampStorage] getDefaultSignStampName:', name ? 'Found' : 'Not found');
    return name;
  } catch (error) {
    logger.error('[signStampStorage] Failed to retrieve default sign/stamp name:', error);
    return null;
  }
}

/**
 * Saves the default sign/stamp name in localStorage
 * @param name - Sign/stamp name to save
 */
export function setDefaultSignStampName(name: string): void {
  try {
    logger.info('[signStampStorage] setDefaultSignStampName: Saving name');
    localStorage.setItem(DEFAULT_SIGN_STAMP_NAME_KEY, name);
    logger.info('[signStampStorage] Default sign/stamp name saved successfully');
  } catch (error) {
    logger.error('[signStampStorage] Failed to save default sign/stamp name:', error);
    throw error;
  }
}

/**
 * Removes the default sign/stamp name from localStorage
 */
export function clearDefaultSignStampName(): void {
  try {
    logger.info('[signStampStorage] clearDefaultSignStampName: Clearing name');
    localStorage.removeItem(DEFAULT_SIGN_STAMP_NAME_KEY);
    logger.info('[signStampStorage] Default sign/stamp name cleared successfully');
  } catch (error) {
    logger.error('[signStampStorage] Failed to clear default sign/stamp name:', error);
  }
}

/**
 * Retrieves the default sign/stamp role from localStorage
 * @returns Sign/stamp role or null if not set
 */
export function getDefaultSignStampRole(): string | null {
  try {
    const role = localStorage.getItem(DEFAULT_SIGN_STAMP_ROLE_KEY);
    logger.info('[signStampStorage] getDefaultSignStampRole:', role ? 'Found' : 'Not found');
    return role;
  } catch (error) {
    logger.error('[signStampStorage] Failed to retrieve default sign/stamp role:', error);
    return null;
  }
}

/**
 * Saves the default sign/stamp role in localStorage
 * @param role - Sign/stamp role to save
 */
export function setDefaultSignStampRole(role: string): void {
  try {
    logger.info('[signStampStorage] setDefaultSignStampRole: Saving role');
    localStorage.setItem(DEFAULT_SIGN_STAMP_ROLE_KEY, role);
    logger.info('[signStampStorage] Default sign/stamp role saved successfully');
  } catch (error) {
    logger.error('[signStampStorage] Failed to save default sign/stamp role:', error);
    throw error;
  }
}

/**
 * Removes the default sign/stamp role from localStorage
 */
export function clearDefaultSignStampRole(): void {
  try {
    logger.info('[signStampStorage] clearDefaultSignStampRole: Clearing role');
    localStorage.removeItem(DEFAULT_SIGN_STAMP_ROLE_KEY);
    logger.info('[signStampStorage] Default sign/stamp role cleared successfully');
  } catch (error) {
    logger.error('[signStampStorage] Failed to clear default sign/stamp role:', error);
  }
}
